import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { QrCode, Scan, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

export const QRWalletCode = ({ walletId, userName, userType }) => {
  const [showQR, setShowQR] = useState(false);
  const [copied, setCopied] = useState(false);

  const qrData = JSON.stringify({
    walletId: walletId,
    name: userName,
    type: userType,
    app: 'MojoMoney'
  });

  const copyWalletId = () => {
    navigator.clipboard.writeText(walletId);
    setCopied(true);
    toast.success('Wallet ID copied');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <Button
        onClick={() => setShowQR(true)}
        variant="outline"
        className="border-[#F2B10C]"
        size="sm"
      >
        <QrCode size={16} className="mr-2" />
        Show QR Code
      </Button>

      <Dialog open={showQR} onOpenChange={setShowQR}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1B0C34]">Your Wallet QR Code</DialogTitle>
          </DialogHeader>
          
          <div className="flex flex-col items-center space-y-4">
            <div className="p-4 bg-white rounded-lg">
              <QRCodeSVG 
                value={qrData}
                size={256}
                level="H"
                includeMargin={true}
              />
            </div>

            <div className="w-full p-3 bg-[#FAF5F0] rounded">
              <Label className="text-xs text-[#1B0C34]/60">Wallet ID</Label>
              <div className="flex items-center gap-2 mt-1">
                <p className="font-mono text-sm font-bold text-[#1B0C34] flex-1">{walletId}</p>
                <Button
                  onClick={copyWalletId}
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0"
                >
                  {copied ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
                </Button>
              </div>
            </div>

            <p className="text-xs text-center text-[#1B0C34]/70">
              Share this QR code to receive payments from other Mojo Money users
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const QRScanner = ({ onScan, onClose }) => {
  const [manualInput, setManualInput] = useState('');
  const [useManual, setUseManual] = useState(false);

  const handleManualSubmit = () => {
    if (manualInput.trim()) {
      onScan(manualInput.trim());
    }
  };

  return (
    <div className="space-y-4">
      {!useManual ? (
        <div className="text-center space-y-4">
          <div className="p-8 bg-[#1B0C34]/5 rounded-lg border-2 border-dashed border-[#F2B10C]">
            <Scan size={64} className="mx-auto text-[#F2B10C] mb-4" />
            <p className="text-sm text-[#1B0C34]/70">
              Camera scanner would appear here
            </p>
            <p className="text-xs text-[#1B0C34]/50 mt-2">
              (Camera access required on mobile devices)
            </p>
          </div>

          <Button
            onClick={() => setUseManual(true)}
            variant="outline"
            className="w-full"
          >
            Enter Wallet ID Manually
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <Label htmlFor="manual_wallet_id">Enter Wallet ID or Payment Token</Label>
            <Input
              id="manual_wallet_id"
              placeholder="MW... or MP..."
              value={manualInput}
              onChange={(e) => setManualInput(e.target.value.toUpperCase())}
              className="font-mono"
            />
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleManualSubmit}
              className="flex-1 bg-[#145539] hover:bg-[#1e7049] text-white"
              disabled={!manualInput.trim()}
            >
              Continue
            </Button>
            <Button
              onClick={() => setUseManual(false)}
              variant="outline"
              className="flex-1"
            >
              Scan QR
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
